/**
 * Une lo que dice el motor con lo que se registró a mano, y arma el informe de
 * posiciones con los campos de WOX.
 *
 * Puro: sin reloj, sin red y con orden total (constitución, Art. 1). Corre en el
 * navegador, pero vive en el núcleo para que la regla de unión tenga pruebas
 * propias y no dependa de una pantalla.
 *
 * Dos fuentes, cada una con su verdad:
 * - el **motor** sabe si un equipo habló, cuándo y dónde estaba;
 * - el **registro** sabe qué equipo es, qué SIM tiene, qué placa y de quién es.
 * Se enlazan por el `deviceId` opaco del motor; el IMEI nunca viaja.
 */

import type { RastreoPosition, RastreoVehicle } from "../contracts/rastreo.ts";
import type { RegistroCliente, RegistroEquipo, RelacionPlaca } from "../contracts/rastreo-registro.ts";
import { normalizePlate, searchKey } from "./normalize.ts";

/**
 * Estado de una fila, en el orden en que se le dice a una persona.
 * - `online` / `offline` / `never`: lo que dice el motor sobre la conectividad.
 * - `unlinked`: registrado, pero sin equipo del motor asociado.
 * - `no_motor_data`: registrado y enlazado, pero el motor no lo reporta (caído o equipo retirado).
 */
export type RowState = "online" | "offline" | "never" | "unlinked" | "no_motor_data";

export type RastreoRow = {
  /** `reg:<id>` para lo registrado, `motor:<deviceId>` para lo que solo conoce el motor. */
  key: string;
  name: string;
  equipo: RegistroEquipo | null;
  cliente: RegistroCliente | null;
  vehicle: RastreoVehicle | null;
  state: RowState;
  /** Lo que Sync/Softseguros, HighLevel y Alegra dicen de su placa vigente, si hay. */
  relacion: RelacionPlaca | null;
};

export type RastreoGroup = { key: string; title: string; rows: RastreoRow[] };

export const UNASSIGNED_GROUP = "sin-cliente";
export const UNREGISTERED_GROUP = "sin-registrar";

const byText = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** Orden total: nombre sin tildes ni mayúsculas, y la clave como desempate. */
const byRow = (a: RastreoRow, b: RastreoRow) => byText(searchKey(a.name), searchKey(b.name)) || byText(a.key, b.key);

function stateOf(vehicle: RastreoVehicle | null, linked: boolean): RowState {
  if (vehicle) {
    if (vehicle.connectionStatus === "online") return "online";
    if (vehicle.connectionStatus === "offline") return "offline";
    return "never";
  }
  return linked ? "no_motor_data" : "unlinked";
}

/**
 * Una fila por equipo registrado, más una por cada equipo del motor que nadie
 * registró. `vehicles` es `null` si el motor no respondió: el registro se sigue
 * viendo igual (RR-14).
 */
export function mergeRastreo(
  equipos: RegistroEquipo[],
  clientes: RegistroCliente[],
  vehicles: RastreoVehicle[] | null,
  relaciones: RelacionPlaca[] = [],
): RastreoRow[] {
  const clientById = new Map(clientes.map((cliente) => [cliente.id, cliente]));
  const relationByPlate = new Map(relaciones.map((relacion) => [normalizePlate(relacion.placa), relacion]));
  const vehicleById = new Map((vehicles ?? []).map((vehicle) => [vehicle.deviceId, vehicle]));
  const linked = new Set<string>();

  const rows: RastreoRow[] = equipos.map((equipo) => {
    const vehicle = equipo.motorDeviceId ? (vehicleById.get(equipo.motorDeviceId) ?? null) : null;
    if (equipo.motorDeviceId) linked.add(equipo.motorDeviceId);
    return {
      key: `reg:${equipo.id}`,
      name: equipo.nombre,
      equipo,
      cliente: equipo.clienteId ? (clientById.get(equipo.clienteId) ?? null) : null,
      vehicle,
      state: stateOf(vehicle, equipo.motorDeviceId !== null),
      relacion: equipo.placa ? (relationByPlate.get(normalizePlate(equipo.placa)) ?? null) : null,
    };
  });

  for (const vehicle of vehicles ?? []) {
    if (linked.has(vehicle.deviceId)) continue;
    rows.push({
      key: `motor:${vehicle.deviceId}`,
      name: vehicle.name ?? vehicle.model ?? `Equipo ${vehicle.deviceId.slice(0, 8)}`,
      equipo: null,
      cliente: null,
      vehicle,
      state: stateOf(vehicle, true),
      relacion: null,
    });
  }

  return rows.sort(byRow);
}

/**
 * Agrupa como WOX: un grupo por cliente, en orden alfabético; después los
 * registrados sin cliente y al final lo que el motor reporta y nadie registró.
 */
export function groupRows(rows: RastreoRow[]): RastreoGroup[] {
  const groups = new Map<string, RastreoGroup>();
  for (const row of rows) {
    const key = !row.equipo ? UNREGISTERED_GROUP : row.cliente ? `cliente:${row.cliente.id}` : UNASSIGNED_GROUP;
    const title = !row.equipo ? "Sin registrar" : row.cliente ? row.cliente.nombre : "Sin cliente";
    const group = groups.get(key) ?? { key, title, rows: [] };
    group.rows.push(row);
    groups.set(key, group);
  }
  const rank = (group: RastreoGroup) =>
    group.key === UNREGISTERED_GROUP ? 2 : group.key === UNASSIGNED_GROUP ? 1 : 0;
  return [...groups.values()]
    .map((group) => ({ ...group, rows: [...group.rows].sort(byRow) }))
    .sort((a, b) => rank(a) - rank(b) || byText(searchKey(a.title), searchKey(b.title)) || byText(a.key, b.key));
}

export type RowFilter = "todos" | "en_linea" | "sin_conexion" | "pendientes";

export const ROW_FILTER_LABEL: Record<RowFilter, string> = {
  todos: "Todos",
  en_linea: "En línea",
  sin_conexion: "Sin conexión",
  pendientes: "Pendientes de registrar o vincular",
};

function matchesFilter(row: RastreoRow, filter: RowFilter): boolean {
  if (filter === "en_linea") return row.state === "online";
  if (filter === "sin_conexion") return row.state === "offline" || row.state === "never" || row.state === "no_motor_data";
  if (filter === "pendientes") return row.equipo === null || row.state === "unlinked";
  return true;
}

/** Busca por nombre, placa, cliente, modelo, ciudad o los últimos dígitos del IMEI (RR-03). */
export function filterRows(rows: RastreoRow[], query: string, filter: RowFilter): RastreoRow[] {
  const needle = searchKey(query);
  return rows.filter((row) => {
    if (!matchesFilter(row, filter)) return false;
    if (!needle) return true;
    const haystack = [
      row.name,
      row.equipo?.placa,
      row.equipo?.modelo,
      row.equipo?.ciudad,
      row.equipo?.identificadorFinal,
      row.cliente?.nombre,
      row.vehicle?.name,
      row.vehicle?.model,
    ]
      .filter((value): value is string => typeof value === "string")
      .map(searchKey);
    return haystack.some((value) => value.includes(needle));
  });
}

/** Equipos del motor que todavía no están enlazados a ningún registro: los que ofrece el formulario. */
export function unlinkedVehicles(vehicles: RastreoVehicle[], equipos: RegistroEquipo[], keep: string | null = null) {
  const used = new Set(equipos.map((equipo) => equipo.motorDeviceId).filter((id): id is string => id !== null));
  return vehicles
    .filter((vehicle) => vehicle.deviceId === keep || !used.has(vehicle.deviceId))
    .sort((a, b) => byText(a.name ?? "", b.name ?? "") || byText(a.deviceId, b.deviceId));
}

/* --------------------------------------------------------------- *
 * Ficha manual frente a Sync/Softseguros y HighLevel, por la placa
 * --------------------------------------------------------------- */

/**
 * La ficha del cliente es el registro del área de GPS y se llena a mano. No se reemplaza con
 * lo que dicen los otros sistemas: se pone al lado, para que una diferencia se vea y la
 * resuelva una persona (AGENTS.md § 4: ante una contradicción no se adivina cuál vale).
 */
export type ComparisonState = "coincide" | "difiere" | "sin_fuente" | "sin_manual";

export type ComparisonRow = {
  campo: "Titular" | "Documento" | "Póliza" | "Ciudad";
  manual: string | null;
  fuente: string | null;
  /** Sistema del que salió el dato de la fuente: `sync`, `highlevel`, `alegra`… */
  sistema: string | null;
  observadoEn: string | null;
  estado: ComparisonState;
};

const digits = (value: string) => value.replace(/\D/gu, "");
const compact = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/gu, "");

/**
 * Dos documentos son el mismo si tienen los mismos dígitos, o si uno trae el dígito de
 * verificación del NIT y el otro no: con o sin ese último dígito, es la misma empresa.
 */
export function sameDocument(a: string, b: string): boolean {
  const left = digits(a);
  const right = digits(b);
  if (!left || !right) return false;
  if (left === right) return true;
  if (Math.abs(left.length - right.length) !== 1) return false;
  return left.length > right.length ? left.slice(0, -1) === right : right.slice(0, -1) === left;
}

export function compareWithSources(cliente: RegistroCliente | null, relacion: RelacionPlaca | null): ComparisonRow[] {
  const rows: Array<[ComparisonRow["campo"], string | null, string | null, string, (a: string, b: string) => boolean]> = [
    ["Titular", cliente?.nombre ?? null, relacion?.titularNombre ?? null, "titular_nombre", (a, b) => searchKey(a) === searchKey(b)],
    ["Documento", cliente?.documento ?? null, relacion?.titularDocumento ?? null, "titular_documento", sameDocument],
    ["Póliza", cliente?.poliza ?? null, relacion?.numeroPoliza ?? null, "numero_poliza", (a, b) => compact(a) === compact(b)],
    ["Ciudad", cliente?.ciudad ?? null, relacion?.ciudad ?? null, "ciudad", (a, b) => searchKey(a) === searchKey(b)],
  ];
  return rows.map(([campo, manual, fuente, key, equal]) => {
    const origen = relacion?.fuentes[key] ?? null;
    const estado: ComparisonState =
      fuente === null ? "sin_fuente" : manual === null ? "sin_manual" : equal(manual, fuente) ? "coincide" : "difiere";
    return { campo, manual, fuente, sistema: origen?.system ?? null, observadoEn: origen?.observedAt ?? null, estado };
  });
}

/* --------------------------------------------------------------- *
 * Informe de posiciones: los campos de WOX
 * --------------------------------------------------------------- */

/**
 * Los campos del informe de posiciones de WOX, en su orden y con su nombre. `sent` dice si el GPS
 * (protocolo GT06, el único que hoy hablan los equipos) manda ese dato en alguna trama. Si no lo
 * manda, la columna existe pero dice «sin dato»: no se calcula ni se rellena (constitución, Art. 3).
 * Algunos solo vienen en ciertas tramas (celda, batería y corte llegan con las de estado o alarma),
 * así que una posición suelta puede salir sin ellos.
 */
export const POSITION_FIELDS = [
  { key: "serverTime", label: "Tiempo de Servidor", sent: true, hint: "Cuándo llegó la trama al motor." },
  { key: "latitude", label: "Latitud", sent: true, hint: "Grados." },
  { key: "longitude", label: "Longitud", sent: true, hint: "Grados." },
  { key: "altitude", label: "Altitud", sent: false, hint: "El GT06 no manda altitud." },
  { key: "speed", label: "Velocidad", sent: true, hint: "km/h, tal como la mandó el equipo." },
  { key: "course", label: "Ángulo", sent: true, hint: "Grados desde el norte." },
  { key: "status", label: "status", sent: false, hint: "Estado general de WOX. El GT06 no lo manda." },
  { key: "ignition", label: "ignition", sent: true, hint: "Ignición encendida o apagada, según el equipo." },
  { key: "charge", label: "charge", sent: true, hint: "Alimentación externa conectada. Llega en las tramas de estado y alarma." },
  { key: "blocked", label: "blocked", sent: true, hint: "Corte de motor activo según el equipo. Solo se lee: desde aquí no se manda ningún comando." },
  { key: "batteryLevel", label: "batterylevel", sent: true, hint: "Batería interna del GPS, en %. Llega en las tramas de estado y alarma." },
  { key: "rssi", label: "rssi", sent: true, hint: "Nivel de señal GSM (en GT06 va de 0 a 4). Llega en las tramas de estado y alarma." },
  { key: "sequence", label: "sequence", sent: false, hint: "Número de secuencia de WOX. El motor no lo guarda." },
  { key: "distance", label: "distance", sent: false, hint: "Distancia desde el punto anterior. No se calcula: solo se muestra lo que manda el equipo." },
  { key: "totalDistance", label: "totaldistance", sent: true, hint: "Odómetro que manda el equipo (no todos los tipos de trama lo traen)." },
  { key: "motion", label: "motion", sent: true, hint: "Si el equipo reporta que se mueve." },
  { key: "valid", label: "valid", sent: true, hint: "Si el GPS tenía señal fija." },
  { key: "engineHours", label: "enginehours", sent: false, hint: "Horas de motor. El GT06 no las manda." },
  { key: "iccid", label: "iccid", sent: false, hint: "Identifica la SIM: no se publica en la API ni se muestra." },
  { key: "alarm1Status", label: "alarm1status", sent: false, hint: "Campo de WOX. El GT06 no lo manda." },
  { key: "otherStatus", label: "otherstatus", sent: false, hint: "Campo de WOX. El GT06 no lo manda." },
  { key: "alarm2Status", label: "alarm2status", sent: false, hint: "Campo de WOX. El GT06 no lo manda." },
  { key: "alarm3Status", label: "alarm3status", sent: false, hint: "Campo de WOX. El GT06 no lo manda." },
  { key: "engineStatus", label: "enginestatus", sent: false, hint: "Estado del motor de WOX. El GT06 no lo manda." },
  { key: "mcc", label: "mcc", sent: true, hint: "Código de país de la antena celular." },
  { key: "mnc", label: "mnc", sent: true, hint: "Código de operador de la antena celular." },
  { key: "lac", label: "lac", sent: true, hint: "Código de área de la antena celular." },
  { key: "cid", label: "cid", sent: true, hint: "Identificador de la antena celular." },
  { key: "event", label: "event", sent: true, hint: "Alarma que mandó el equipo en esa trama, si la hubo." },
  { key: "archive", label: "archive", sent: true, hint: "Sí: el equipo reenvió esta posición desde su memoria." },
  { key: "geofence", label: "geofence", sent: true, hint: "Entrada o salida de una geocerca del propio equipo, si la mandó como alarma." },
  { key: "sat", label: "sat", sent: true, hint: "Satélites que veía el GPS." },
] as const;

export type PositionFieldKey = (typeof POSITION_FIELDS)[number]["key"];

export type PositionValue = number | boolean | string | null;
export type PositionRecord = { id: number } & Record<PositionFieldKey, PositionValue>;

/** Columnas que se ven por omisión: las que el GPS sí manda. El resto se marca en «Columnas». */
export const DEFAULT_POSITION_COLUMNS: PositionFieldKey[] = POSITION_FIELDS.filter((field) => field.sent).map(
  (field) => field.key,
);

const numberAttr = (attributes: RastreoPosition["attributes"], key: string) => {
  const value = attributes[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const booleanAttr = (attributes: RastreoPosition["attributes"], key: string) => {
  const value = attributes[key];
  return typeof value === "boolean" ? value : null;
};

const textAttr = (attributes: RastreoPosition["attributes"], key: string) => {
  const value = attributes[key];
  return typeof value === "string" && value !== "" ? value : null;
};

/**
 * Un punto del motor como registro del informe. Lo que el equipo no mandó queda `null`
 * —«sin dato»—, nunca 0 (constitución, Art. 3).
 */
export function toPositionRecords(points: RastreoPosition[]): PositionRecord[] {
  return points.map((point, index) => {
    const alarm = textAttr(point.attributes, "alarm");
    const a = point.attributes;
    return {
      id: index + 1,
      serverTime: point.serverReceivedAt,
      latitude: point.latitude,
      longitude: point.longitude,
      altitude: point.altitudeM,
      speed: point.speedKph,
      course: point.headingDeg,
      status: null,
      ignition: point.ignition === "unknown" ? null : point.ignition === "on",
      charge: booleanAttr(a, "externalPower"),
      blocked: booleanAttr(a, "cutActive"),
      batteryLevel: numberAttr(a, "batteryPct"),
      rssi: numberAttr(a, "gsmLevel"),
      sequence: null,
      distance: null,
      totalDistance: numberAttr(a, "odometerM"),
      motion: booleanAttr(a, "motion"),
      valid: point.fixValid,
      engineHours: null,
      iccid: null,
      alarm1Status: null,
      otherStatus: null,
      alarm2Status: null,
      alarm3Status: null,
      engineStatus: null,
      mcc: numberAttr(a, "mcc"),
      mnc: numberAttr(a, "mnc"),
      lac: numberAttr(a, "lac"),
      cid: numberAttr(a, "cellId"),
      event: alarm,
      archive: booleanAttr(a, "resent"),
      geofence: alarm !== null && alarm.startsWith("geocerca_") ? alarm : null,
      sat: point.satellites,
    };
  });
}
